import React, { useRef, useState } from "react";
import CommonTimeLineCard from "../../CommonTimeLineCard";
import BarClick from "../../TimelineGraph";
import {
  displayBrowser,
  displayOS,
  displayPlatform,
} from "../DisplayEnvironment/displayenv";

import TimelineAccordionCard from "../TimelineCommonComponents/TimelineAccordionCard";

import {
  GlobalConfiguration,
  IntervalConfiguration,
  TimelineConfiguration,
} from "../../../utils/configurationProvider";
import Loader from "../../Loader/Index";
import ICONS from "../../../assets/Icons";

/**
 * TimelinePopupContent Component
 *
 * Displays full timeline view with:
 *  - Approval Timeline (Stage 1)
 *  - Distribution Timeline (Stage 2)
 *  - Statistics (duration + graph)
 *  - Expandable accordion cards
 *
 * @component
 *
 * @param {Object} props
 *
 * @param {Detail[]} props.logsData
 * Main transaction data array (usually first index is used).
 *
 * @param {OnClickHandler} [props.handleApprovalClick]
 * Triggered when user clicks on Approval summary card
 *
 * @param {OnClickHandler} [props.handleDistributionClick]
 * Triggered when user clicks on Distribution summary card
 *
 * @param {boolean} props.showDistributionRightIcon
 * Controls visibility of right arrow icon in Distribution card
 *
 * @param {boolean} props.showApprovalLeftIcon
 * Controls visibility of left arrow icon in Approval card
 *
 * @param {boolean} props.showApprovalTimeline
 * Determines whether Approval Timeline section is visible
 *
 * @param {boolean[]} props.openApprovalTimeline
 * Tracks expanded/collapsed state of approval accordion items
 *
 *
 * @param {Function} props.setOpenApprovalTimeline
 * Setter for updating approval accordion state
 *
 * @param {boolean} [props.showDistributionTimeline=false]
 * Determines whether Distribution Timeline section is visible
 *
 * @param {boolean[]} [props.openDistributionTimeline=[]]
 * Tracks expanded/collapsed state of distribution accordion items
 *
 * @param {Function} [props.setOpenDistributionTimeline]
 * Setter for updating distribution accordion state
 *
 * @param {("single" | "double")} props.cardType
 * Layout type:
 *  - "single": Only approval
 *  - "double": Approval + Distribution
 *
 * @param {boolean} props.popUpMode
 * Enables popup mode (restricts interactions and styling)
 *
 * @returns {JSX.Element}
 */

const TimelinePopupContent = ({
  logsData,
  handleApprovalClick = () => {},
  handleDistributionClick = () => {},
  showDistributionRightIcon,
  showApprovalLeftIcon,
  showApprovalTimeline,
  showDistributionTimeline = false,
  openApprovalTimeline,
  openDistributionTimeline = [],
  setOpenApprovalTimeline,
  setOpenDistributionTimeline = () => {},
  cardType,
  popUpMode,
}) => {
  const cardRefs = useRef({});
  const cardRefsDist = useRef({});
  const [isAnimationPopupOpen, setIsAnimationPopupOpen] = useState(false);
  const renderApprovalData = (obj) => {
    return Object.keys(obj).map((key, index, array) => {
      const isLastItem =
        index === array.length - GlobalConfiguration?.lengthCheckOne;
      if (key !== "action") {
        return (
          <>
            <div
              key={key}
              className={`flex flex-row items-center ${
                !isLastItem ? "mb-margin-md" : ""
              }`}
            >
              <div className="w-icon-xs border-b-border border-solid border-text-secondary"></div>

              <p
                key={key}
                className="w-width-full text-text-secondary text-sm text-left font-normal ml-margin-xs make-first-letter-small break-words"
              >{`"${key}": "${obj[key]}"`}</p>
            </div>
          </>
        );
      }
      return null;
    });
  };

  const getTotalDuration = (logs) => {
    if (!logs || logs.length < TimelineConfiguration.barWidthMedium)
      return "0s";

    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    );
    const start = new Date(sortedLogs[0].timestamp);
    const end = new Date(
      sortedLogs[sortedLogs.length - GlobalConfiguration.lengthCheckOne]
        .timestamp,
    );
    const diffMs = end - start;

    return formatDuration(diffMs);
  };

  const getTotalDurationApproval = (logs) => {
    if (!logs || logs.length < TimelineConfiguration.barWidthMedium)
      return "0s";

    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    );
    const start = new Date(sortedLogs[0].timestamp);
    const end = new Date(
      sortedLogs[sortedLogs.length - GlobalConfiguration.lengthCheckOne]
        .timestamp,
    );
    const diffMs = end - start;

    return formatDuration(diffMs);
  };

  const formatDuration = (ms) => {
    if (ms <= GlobalConfiguration.lengthCheckZero) return "0s";

    if (ms < TimelineConfiguration.totalSeconds) return "1s";

    const seconds = Math.floor(ms / TimelineConfiguration.totalSeconds);
    const minutes = Math.floor(seconds / TimelineConfiguration.oneHour);
    const hours = Math.floor(minutes / TimelineConfiguration.oneHour);
    const days = Math.floor(hours / TimelineConfiguration.oneDay);

    if (days > GlobalConfiguration.lengthCheckZero) {
      const remHours = hours % TimelineConfiguration.oneDay;
      const remMinutes = minutes % TimelineConfiguration.oneHour;
      return `${days}d ${remHours}h ${remMinutes}m`;
    } else if (hours > GlobalConfiguration.lengthCheckZero) {
      const remMinutes = minutes % TimelineConfiguration.oneHour;
      return `${hours}h ${remMinutes}m`;
    } else if (minutes > GlobalConfiguration.lengthCheckZero) {
      const remSeconds = seconds % TimelineConfiguration.oneHour;
      return `${minutes}m ${remSeconds}s`;
    } else {
      const remMs = ms % TimelineConfiguration.totalSeconds;
      return remMs > GlobalConfiguration.lengthCheckZero
        ? `${seconds}s ${remMs}ms`
        : `${seconds}s`;
    }
  };

  const logs = logsData?.[0]?.distribution_logs || [];
  const Approvallogs = logsData?.[0]?.logs || [];
  const totalDuration = getTotalDuration(logs);

  const totalDurationApproval = getTotalDurationApproval(Approvallogs);

  const handleCardDistributionClick = (index) => {
    setOpenDistributionTimeline((prev) => {
      const newState = Array(prev.length).fill(false);
      newState[index] = !prev[index];
      return newState;
    });
    setOpenApprovalTimeline(Array(logsData[0]?.logs?.length).fill(false));

    setTimeout(() => {
      cardRefsDist.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, IntervalConfiguration.intervalExtraSmall);
  };

  const handleCardClick = (index) => {
    setOpenApprovalTimeline((prev) => {
      const newState = Array(prev.length).fill(false);
      newState[index] = !prev[index];
      return newState;
    });

    setOpenDistributionTimeline(
      Array(logsData[0]?.distribution_logs?.length).fill(false),
    );

    setTimeout(() => {
      cardRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, IntervalConfiguration.intervalExtraSmall);
  };

  return (
    <div className="h-full">
      {logsData?.length !== 0 ? (
        <div className=" bg-primary-bg">
          <CommonTimeLineCard
            logsData={logsData}
            handleApprovalClick={handleApprovalClick}
            handleDistributionClick={handleDistributionClick}
            showDistributionRightIcon={showDistributionRightIcon}
            showApprovalLeftIcon={showApprovalLeftIcon}
            showApprovalTimeline={showApprovalTimeline}
            cardType={cardType}
            showDistributionTimeline={showDistributionTimeline}
            popUpMode={popUpMode}
          />

          {showApprovalTimeline ? (
            <>
              <div className=" overflow-y-auto  ">
                <div
                  className={` ${showApprovalTimeline ? "block" : "hidden"} mx-margin-md mt-margin-md `}
                >
                  <div className="bg-primary-card  h-auto rounded-border-radius-md relative pt-padding-sm  pr-padding-md ">
                    <div className=" flex pl-padding-md">
                      <div className=" w-icon-md">
                        <img
                          src="https://q2pay.in/new_assets/common/settings/statistics.svg"
                          className="w-icon-md"
                          alt=""
                        />
                      </div>
                      <p className="text-text-secondary text-font-sm font-semibold ml-margin-sm   ">
                        Approval statistics
                      </p>{" "}
                    </div>

                    {/* Right side timeline */}
                    <div className="flex-1">
                      <BarClick
                        type={"ApprovalTimeline"}
                        viewTxnDetails={logsData}
                        handleCardClick={handleCardClick}
                        handleCardDistributionClick={
                          handleCardDistributionClick
                        }
                        totalDuration={totalDuration}
                        isAnimationPopupOpen={isAnimationPopupOpen}
                        setIsAnimationPopupOpen={setIsAnimationPopupOpen}
                      />
                    </div>

                    <div className="flex items-center justify-center ml-margin-sm mt-margin-sm">
                      {logsData?.[0]?.logs?.length >
                        GlobalConfiguration.lengthCheckOne && (
                        <>
                          <div className=" flex items-center justify-center mb-margin-sm">
                            <img
                              src="https://q2pay.in/new_assets/common/detail_page/outline_clock.svg"
                              className=" w-icon-xs flex items-start justify-center mr-margin-xs"
                              alt=""
                            />
                            <p className="text-text-secondary text-font-xs font-normal">
                              {totalDurationApproval}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className="  px-padding-md py-padding-md  w-full overflow-y-scroll "
                  style={{ scrollbarWidth: "none" }}
                >
                  {logsData?.length >= 1 &&
                    [...logsData[0]?.logs]?.reverse().map((account, index) => {
                      const trackCount =
                        (index + GlobalConfiguration.lengthCheckOne) %
                        !logsData[0]?.logs?.length;
                      const logs_length = logsData[0]?.logs?.length;
                      const num = index + GlobalConfiguration.lengthCheckOne;
                      return (
                        <>
                          <div
                            key={index}
                            className="flex fex-row items-start w-full space-x-10 relative"
                          >
                            <div
                              className={`absolute border-l-borderthickness border-solid  border-primary-nav w-tl-width-3 ${
                                num === logs_length
                                  ? "h-icon-lg duration-animation-medium ease-in-out"
                                  : openApprovalTimeline[index]
                                    ? "h-full duration-animation-medium ease-in-out"
                                    : "h-full duration-animation-medium ease-in-out"
                              }`}
                            >
                              <div className="absolute flex flex-row w-full">
                                <img
                                  src={ICONS?.TIMELINE_DOT}
                                  className="w-icon-md h-icon-md mt-margin-lg ml-margin-left-card"
                                  alt=""
                                />
                                <div className="w-icon-md border-t-borderthickness mt-margin-xl ml-margin-sm border-solid  border-primary-nav"></div>
                              </div>

                              {showApprovalTimeline && num === 1 && (
                                <>
                                  <div className="absolute border-l-borderthickness border-solid border-primary-nav  h-timeline-popup ml-margin-left-top mt-margin-left"></div>
                                  <div className="absolute border-t-borderthickness border-solid border-primary-nav w-timeline-width ml-margin-base mt-margin-left"></div>
                                  <div
                                    className="absolute border-r-borderthickness border-solid border-primary-nav h-timeline-popup-leftline  mt-no-txn-found"
                                    style={{
                                      marginLeft: "var(--timeline-width)",
                                    }}
                                  ></div>
                                </>
                              )}
                            </div>

                            <div
                              className=" w-full"
                              ref={(el) => (cardRefs.current[index] = el)}
                            >
                              <TimelineAccordionCard
                                account={account}
                                isAnimationPopupOpen={isAnimationPopupOpen}
                                setIsAnimationPopupOpen={
                                  setIsAnimationPopupOpen
                                }
                                openApprovalTimeline={
                                  openApprovalTimeline[index]
                                }
                                renderApprovalData={renderApprovalData}
                                borderRequired={true}
                                handleApprovalTimeline={() =>
                                  handleCardClick(index)
                                }
                                FirstDivHeaderText={
                                  account.action === "Add Status"
                                    ? account.remark.toLowerCase()
                                    : account.action.toLowerCase()
                                }
                                FirstDivSubtext="Action"
                                SecondDivCondition={account?.browser_name}
                                SecondtDivImage={
                                  "https://q2pay.in/new_assets/common/timeline_icons/" +
                                  displayBrowser(account?.browser_name) +
                                  ".svg"
                                }
                                SecondDivHeaderText={
                                  account?.browser_name ===
                                  "Microsoft Edge (Chromium)"
                                    ? "Microsoft Edge"
                                    : account.browser_name
                                }
                                SecondDivSubHeaderText="Browser"
                                ThirdDivCondition={account?.platform}
                                ThirdDivImage={
                                  "https://q2pay.in/new_assets/common/timeline_icons/" +
                                  displayPlatform(account?.platform) +
                                  ".svg"
                                }
                                ThirdDivHeaderText={account?.platform}
                                ThirdDivSubHeaderText=" Platform"
                                LastDivArrowIcon1="https://q2pay.in/new_assets/common/arrows/arrow_up_white.svg"
                                LastDivArrowIcon2="https://q2pay.in/new_assets/common/arrows/arrow_down_white.svg"
                                imgColor="var(--text-primary)"
                                FourthDivCondition={account?.os_version}
                                FourthDivImage={
                                  "https://q2pay.in/new_assets/common/timeline_icons/" +
                                  displayOS(account?.os_version) +
                                  ".svg"
                                }
                                FourthDivHeaderText={account?.os_version}
                                FourthDivSubHeaderText="Operating system"
                              />
                            </div>
                          </div>
                          {num !== logs_length && (
                            <>
                              <div className="m-auto h-icon-md border-l-borderthickness border-solid  border-primary-nav"></div>
                            </>
                          )}
                        </>
                      );
                    })}
                </div>
              </div>
            </>
          ) : showDistributionTimeline ? (
            <>
              <div className={` mx-margin-md mt-margin-md `}>
                <div className="  bg-primary-card h-auto rounded-border-radius-md px-padding-md pt-padding-sm ">
                  <div className=" flex w-full justify-between pr-padding-sm">
                    <div className=" flex items-center mb-margin-lg">
                      <img
                        src="https://q2pay.in/new_assets/common/settings/statistics.svg"
                        className=" w-icon-md"
                        alt=""
                      />
                      <p className=" text-text-secondary ml-margin-sm text-font-sm font-semibold">
                        Distribution statistics
                      </p>
                    </div>
                  </div>

                  <BarClick
                    viewTxnDetails={logsData}
                    type={"DistributionTimeLine"}
                    handleCardClick={handleCardClick}
                    handleCardDistributionClick={handleCardDistributionClick}
                  />
                </div>
              </div>
              <div
                className="  py-padding-xl px-padding-md  w-full overflow-y-auto"
                style={{ scrollbarWidth: "none" }}
              >
                {logsData?.length >= GlobalConfiguration.lengthCheckOne &&
                  [...logsData[0]?.distribution_logs]
                    ?.reverse()
                    .map((account, index) => {
                      const trackCount =
                        (index + GlobalConfiguration.lengthCheckOne) %
                          logsData[0]?.distribution_logs?.length ===
                        0;
                      const distributionLogs_length =
                        logsData[0]?.distribution_logs?.length;
                      const num = index + GlobalConfiguration.lengthCheckOne;
                      return (
                        <>
                          <div
                            ref={(el) => (cardRefsDist.current[index] = el)}
                            key={index}
                            className="flex fex-row items-start w-full space-x-10 relative"
                          >
                            <div
                              className={`absolute border-l-borderthickness border-solid border-primary-nav w-tl-width-3 ${
                                num === distributionLogs_length
                                  ? "h-category-icon-height duration-animation-fast ease-in-out"
                                  : openDistributionTimeline[index]
                                    ? "h-full duration-animation-fast ease-in-out"
                                    : "h-full duration-animation-fast ease-in-out"
                              }`}
                            >
                              <div className="absolute flex flex-row w-full">
                                <img
                                  src="https://q2pay.in/new_assets/common/create_flow/timeline_dot.svg"
                                  className="w-icon-md h-icon-md mt-[31px] ml-margin-left-card"
                                  alt=""
                                />
                                <div
                                  className=" border-t-borderthickness mt-margin-xl ml-margin-xs border-solid  border-primary-nav"
                                  style={{
                                    width: "var(--timeline-popup)",
                                  }}
                                ></div>
                              </div>

                              {showDistributionTimeline &&
                                num === GlobalConfiguration.lengthCheckOne && (
                                  <>
                                    <div
                                      className="  "
                                      style={{
                                        position: "absolute",
                                        top: "var(--margin-left)",
                                        left: "var(--timeline-margin-left-negative)",
                                        height: "var(--timeline-left-height)",
                                        borderLeft:
                                          "var(--borderthickness) solid var(--primary-nav)",
                                      }}
                                    ></div>
                                    <div
                                      className="  -mt-margin-left"
                                      style={{
                                        width: "var(--timeline-width)",
                                        position: "absolute",
                                        left: "var(--left-base)",
                                        right: "var(--right-base)",
                                        top: "var(--timeline-margin-negative-vertical-adjust)",
                                        borderTop:
                                          "var(--borderthickness) solid var(--primary-nav)",
                                      }}
                                    ></div>
                                    <div
                                      className="  "
                                      style={{
                                        position: "absolute",
                                        top: "var(--transl-custom)",
                                        left: "var(--left-base)",
                                        width: "var(--timeline-width)",
                                        right: "var(--right-base)",
                                        height:
                                          "var(--timeline-popup-leftline)",
                                        borderRight:
                                          "var(--borderthickness) solid var(--primary-nav)",
                                      }}
                                    ></div>
                                  </>
                                )}
                            </div>
                            <div className=" w-full">
                              <TimelineAccordionCard
                                account={account}
                                isAnimationPopupOpen={isAnimationPopupOpen}
                                setIsAnimationPopupOpen={
                                  setIsAnimationPopupOpen
                                }
                                openApprovalTimeline={
                                  openDistributionTimeline[index]
                                }
                                borderRequired={false}
                                renderApprovalData={renderApprovalData}
                                handleApprovalTimeline={() =>
                                  handleCardDistributionClick(index)
                                }
                                FirstDivHeaderText={
                                  account.action === "Add Status"
                                    ? account.remark.toLowerCase()
                                    : account.action
                                        .replace(/([a-z])([A-Z])/g, "$1 $2")
                                        .replace(/^./, (str) =>
                                          str.toUpperCase(),
                                        )
                                }
                                FirstDivSubtext="Action"
                                LastDivArrowIcon1="https://q2pay.in/new_assets/common/arrows/arrow_up_white.svg"
                                LastDivArrowIcon2="https://q2pay.in/new_assets/common/arrows/arrow_down_white.svg"
                                imgColor="var(--text-primary)"
                              />
                            </div>
                          </div>
                          {num !== distributionLogs_length && (
                            <>
                              <div className="m-auto h-icon-md border-l-borderthickness border-solid border-primary-nav"></div>
                            </>
                          )}
                        </>
                      );
                    })}
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <div className="h-full flex justify-center items-center">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default TimelinePopupContent;
